import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createPortal } from 'react-dom';

import type { ResearchRoom } from '@repo/contracts';

import { canvasKeys, getProject } from '../api/canvas-captures';
import { roomKeys, updateRoomPersonas } from '../api/research-rooms';
import { useFeatureRequest } from '../feature-request/use-feature-request';
import { MAX_ON_PANEL, PanelModal } from './panel-modal';
import { usePersonas } from './use-personas';

/**
 * The project's panel — suggest, bring over, take off — as a modal.
 *
 * Opened from the research room's roster, which is where the panel now lives
 * on screen: the people in the room *are* the panel, so managing them is one
 * click from seeing them rather than a separate button in the top bar.
 *
 * Ticking people on and off saves as it goes, but a room already open keeps
 * the roster it was opened with until told otherwise — so closing re-seats it
 * with whoever is ticked. Without that, unticking somebody and pressing Done
 * changed nothing anyone could see.
 */
export function PanelManager({
  captureId,
  onClose,
  projectId,
  room,
}: {
  /** The screen "suggest more" reads. Absent hides that action. */
  captureId?: string;
  onClose: () => void;
  projectId?: string;
  /** The room open on this screen, if there is one, to re-seat on close. */
  room?: ResearchRoom;
}) {
  const queryClient = useQueryClient();
  const {
    bring,
    bringing,
    personas,
    removeFromProject,
    suggestMore,
    suggesting,
    toggle,
  } = usePersonas({ projectId });

  const customPersona = useFeatureRequest('custom_persona', projectId);

  const projectQuery = useQuery({
    enabled: Boolean(projectId),
    queryFn: () => getProject(projectId!),
    queryKey: canvasKeys.project(projectId ?? 'none'),
    staleTime: Infinity,
  });

  const reseat = useMutation({
    mutationFn: (ids: string[]) => updateRoomPersonas(room!.id, ids),
    onSuccess: (view) => {
      queryClient.setQueryData(roomKeys.forCapture(view.room.captureId), view);
      onClose();
    },
  });

  const close = () => {
    // Who a room would seat — the same cut the server makes opening one.
    const ticked = personas
      .filter((persona) => persona.active)
      .slice(0, MAX_ON_PANEL)
      .map((persona) => persona.id);
    const changed =
      room &&
      !room.endedAt &&
      (ticked.length !== room.personaIds.length ||
        ticked.some((id) => !room.personaIds.includes(id)));
    // Nobody ticked is not a room; the modal says so and stays open.
    if (!changed || ticked.length === 0) {
      if (!changed) onClose();
      return;
    }
    reseat.mutate(ticked);
  };

  // Portalled to the body: opened from inside the room panel, its own
  // stacking context would otherwise keep the board's floating pills (title,
  // fix list) on top of the modal and its backdrop.
  return createPortal(
    <PanelModal
      bringing={bringing}
      featureRequestFailed={customPersona.failed}
      featureRequested={customPersona.requested}
      onBring={projectId ? bring : undefined}
      onClose={close}
      onRemoveFromProject={projectId ? removeFromProject : undefined}
      onRequestFeature={customPersona.request}
      onSuggestMore={
        captureId
          ? () => {
              suggestMore({ captureId });
            }
          : undefined
      }
      onToggle={toggle}
      personas={personas}
      project={projectQuery.data}
      requestingFeature={customPersona.requesting}
      saveError={
        reseat.error
          ? // ApiError ends its message with the status, which is for logs.
            reseat.error.message.replace(/ \(\d{3}\)$/, '') ||
            'Could not update the room. Try again.'
          : null
      }
      saving={reseat.isPending}
      suggesting={suggesting}
    />,
    document.body,
  );
}
